import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  ActivityIndicator,
  FlatList,
  SafeAreaView,
  Text,
  TouchableOpacity,
  View,
  Image,
} from 'react-native';
import {useFocusEffect, useNavigation} from '@react-navigation/native';
import {useIsDesktop} from '../../hooks/useIsDesktop';
import ProductsService from '../../services/ProductsService/ProductsService';
import {useBagStore} from '../../store/BagStore';
import {useUserStore} from '../../store/UserStore';
import {useOffersFilterStore} from '../../store/OffersFilterStore';
import ProductList from '../Home/components/ProductList/ProductList';
import ModalFilter from '../Home/components/ModalFilter/ModalFilter';
import {calculatePriceBasedOnQuantity} from '../../utils/price';
import {styles} from './styles';
import SkeletonLoader from '../../components/SkeletonLoader/SkeletonLoader';
import NotFound from '../Menu/components/NotFound/NotFound';
import {Images} from '../../constants/Images';
import {AppRoutes} from '../../navigation/Routes.types';
import BagModal from '../Home/components/BagModal/BagModal';

const OffersScreen = () => {
  const navigation = useNavigation();
  const isDesktop = useIsDesktop();
  const numColumns = isDesktop ? 5 : 2;
  const {user} = useUserStore();
  const {addProduct, updateProductQuantity} = useBagStore();
  const bagProducts = useBagStore(state => state.products);

  const {
    selectedBrand,
    selectedCategory,
    searchQuery,
    selectedSort,
    selectedPackaging,
    setSelectedBrand,
    setSelectedCategory,
    setSearchQuery,
    setSelectedSort,
    setSelectedPackaging,
    resetFilters,
  } = useOffersFilterStore();

  const [products, setProducts] = useState<IProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterModal, setFilterModal] = useState(false);
  const [packagingFilter, setPackagingFilter] = useState<any[]>([]);

  const PER_PAGE = 30;

  useFocusEffect(
    useCallback(() => {
      return () => {
        resetFilters();
      };
    }, []),
  );

  const fetchProducts = useCallback(
    async (page: number, append = false) => {
      if (!user?.clientCode) return;
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      try {
        const response = await ProductsService.catalogProductsGet(
          user.clientCode,
          PER_PAGE,
          page,
          selectedCategory ?? undefined,
          selectedBrand ?? undefined,
          searchQuery || undefined,
          selectedSort ?? undefined,
          undefined,
          selectedPackaging ?? undefined,
          true,
        );
        const fetched = (response.data?.products ?? []) as IProduct[];

        fetched.forEach(product => {
          const inBag = bagProducts.find(p => p.sku === product.sku);
          if (inBag) {
            product.quantity = inBag.quantity;
            const {price, discountedPrice} = calculatePriceBasedOnQuantity(
              product,
              inBag.quantity,
            );
            product.preco = price;
            (product as any).discountedPrice = discountedPrice;
          }
        });

        if (!append && response?.data?.packagingFilter?.length) {
          setPackagingFilter(response.data.packagingFilter);
        }

        setHasMore(fetched.length === PER_PAGE);

        if (append) {
          setProducts(prev => {
            const skus = new Set(prev.map(p => p.sku));
            return [...prev, ...fetched.filter(p => !skus.has(p.sku))];
          });
        } else {
          setProducts(fetched);
        }
      } catch (error) {
        console.error('OffersScreen fetchProducts error:', error);
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      user?.clientCode,
      selectedBrand,
      selectedCategory,
      searchQuery,
      selectedSort,
      selectedPackaging,
      bagProducts,
    ],
  );

  useEffect(() => {
    setCurrentPage(1);
    fetchProducts(1, false);
  }, [
    user?.clientCode,
    selectedBrand,
    selectedCategory,
    searchQuery,
    selectedSort,
    selectedPackaging,
  ]);

  const handleLoadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchProducts(nextPage, true);
  }, [loading, loadingMore, hasMore, currentPage, fetchProducts]);

  const handleAddBag = (product: IProduct, quantity: number) => {
    const cartKey = `${product.sku}_promo`;
    const {products: currentProducts} = useBagStore.getState();
    if (currentProducts.find(p => (p.cartKey ?? p.sku) === cartKey)) {
      updateProductQuantity(cartKey, quantity);
    } else if (quantity > 0) {
      addProduct({...product, quantity, cartKey});
    }
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedBrand) count++;
    if (selectedCategory) count++;
    if (selectedSort) count++;
    if (selectedPackaging) count++;
    return count;
  };

  const renderItem = ({item}: {item: IProduct}) => (
    <ProductList
      clientCode={user?.clientCode}
      item={item}
      image={item.image}
      title={item.descricao}
      description={item.descricao + ' ' + item.complemento.replace('- R$', '')}
      AddBag={(quantity: number) => handleAddBag(item, quantity)}
      onPress={() =>
        navigation.navigate(AppRoutes.PRODUCT_DETAILS as never, {item} as never)
      }
    />
  );

  const renderFooter = () => {
    if (loadingMore) {
      return (
        <View style={{paddingVertical: 10}}>
          <SkeletonLoader tiny />
        </View>
      );
    }
    return null;
  };

  return (
    <SafeAreaView style={[styles.safeArea, {position: 'relative'}]}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}>
          <Text style={styles.backText}>{'<'}</Text>
        </TouchableOpacity>
        <Text style={styles.headerTitle}>{'🏷️ OFERTAS'}</Text>
        <TouchableOpacity
          onPress={() => setFilterModal(true)}
          style={styles.filterButton}>
          <Text style={styles.filterButtonText}>
            Filtrar{getActiveFiltersCount() > 0 ? ` (${getActiveFiltersCount()})` : ''}
          </Text>
        </TouchableOpacity>
      </View>

      {loading ? (
        <SkeletonLoader numColumns={numColumns} />
      ) : (
        <FlatList
          data={products}
          renderItem={renderItem}
          keyExtractor={(item, index) => 'offer_' + item.sku + index}
          key={numColumns}
          numColumns={numColumns}
          contentContainerStyle={styles.listContent}
          showsVerticalScrollIndicator={false}
          onEndReached={handleLoadMore}
          onEndReachedThreshold={0.1}
          ListFooterComponent={renderFooter}
          ListEmptyComponent={
            <View style={{marginVertical: 50}}>
              <NotFound
                description="Nenhuma oferta disponível no momento"
                title="Ops..."
              />
            </View>
          }
        />
      )}

      <ModalFilter
        visible={filterModal}
        onRequestClose={() => setFilterModal(false)}
        currentSort={selectedSort ?? ''}
        currentCategory={selectedCategory ?? ''}
        currentBrands={selectedBrand ? [selectedBrand] : []}
        currentNews={false}
        currentCombo={false}
        returnFilter={filter => {
          setSelectedCategory(filter.category ?? null);
          setSelectedBrand(filter.brands?.[0] ?? null);
          setSelectedSort(filter.sort ?? null);
          setSelectedPackaging(filter.packaging ?? null);
          setFilterModal(false);
        }}
        cleanFilter={() => {
          resetFilters();
          setFilterModal(false);
        }}
        hasBrand
        hasCategory
        hasOrder
        hasSize
        hasPackaging
        packagingList={packagingFilter}
      />
      <BagModal />
    </SafeAreaView>
  );
};

export default OffersScreen;
